import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api, { billingAPI, referralAPI } from '../services/api';
import { User, CreditCard, Brain, Crown, Loader2, CheckCircle, Camera, Upload, ArrowUpCircle, XCircle, Sparkles, AlertTriangle, Receipt, Download, ShoppingBag, Calendar, Mail, Gift, Copy, Users, Share2, Stethoscope, Key, Link2 } from 'lucide-react';
import { toast } from 'sonner';

// Load Razorpay script
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (document.getElementById('razorpay-script')) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.id = 'razorpay-script';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

// Plan hierarchy for upgrades
const PLAN_HIERARCHY = ['weekly', 'monthly', 'quarterly', 'yearly'];

export const Profile = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [profilePicture, setProfilePicture] = useState(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [orderHistory, setOrderHistory] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState(null);
  const [emailingInvoice, setEmailingInvoice] = useState(null);
  const [referralData, setReferralData] = useState(null);
  const [loadingReferral, setLoadingReferral] = useState(false);
  const [doctorConnections, setDoctorConnections] = useState([]);
  const [connectionCode, setConnectionCode] = useState(null);
  const [doctorAccessPin, setDoctorAccessPin] = useState(null);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [generatingPin, setGeneratingPin] = useState(false);
  const fileInputRef = useRef(null);

  const isPro = user?.role === 'superuser' || user?.role === 'admin';
  const isDoctor = user?.user_type === 'doctor';

  useEffect(() => {
    fetchProfileData();
    fetchOrderHistory();
    fetchReferralData();
    loadRazorpayScript();
  }, []);

  useEffect(() => {
    if (user?.profile_picture) {
      setProfilePicture(user.profile_picture);
    }
  }, [user]);

  const fetchProfileData = async () => {
    try {
      const [subRes, plansRes] = await Promise.all([
        billingAPI.getSubscription(),
        billingAPI.getPlans()
      ]);
      setSubscription(subRes.data);
      setPlans(plansRes.data.plans);
    } catch (error) {
      console.error('Failed to load profile data:', error);
    }
  };

  const fetchOrderHistory = async () => {
    setLoadingOrders(true);
    try {
      const response = await billingAPI.getOrderHistory();
      setOrderHistory(response.data.orders || []);
    } catch (error) {
      console.error('Failed to load order history:', error);
    } finally {
      setLoadingOrders(false);
    }
  };

  const fetchReferralData = async () => {
    setLoadingReferral(true);
    try {
      const response = await referralAPI.getCode();
      setReferralData(response.data);
    } catch (error) {
      console.error('Failed to load referral data:', error);
    } finally {
      setLoadingReferral(false);
    }
  };

  const fetchDoctorConnections = async () => {
    if (isDoctor) return; // Only for patients
    try {
      const response = await api.get('/doctor-portal/connections?status=approved');
      setDoctorConnections(response.data.connections || []);
    } catch (error) {
      console.error('Failed to load doctor connections:', error);
    }
  };

  const generateConnectionCode = async () => {
    setGeneratingCode(true);
    try {
      const response = await api.post('/doctor-portal/patient/generate-code', { expires_in_hours: 24 });
      setConnectionCode(response.data);
      toast.success('Connection code generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate code');
    } finally {
      setGeneratingCode(false);
    }
  };

  const generateDoctorAccessPin = async () => {
    setGeneratingPin(true);
    try {
      const response = await api.post('/doctor-portal/patient/vault/generate-doctor-pin', { expires_in_hours: 24 });
      setDoctorAccessPin(response.data);
      toast.success('Doctor access PIN generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate PIN. Make sure you have set up your Private Vault first.');
    } finally {
      setGeneratingPin(false);
    }
  };

  // Fetch doctor connections when component mounts
  useEffect(() => {
    if (!isDoctor) {
      fetchDoctorConnections();
    }
  }, [isDoctor]);

  const copyReferralCode = () => {
    if (referralData?.code) {
      navigator.clipboard.writeText(referralData.code);
      toast.success('Referral code copied!');
    }
  };

  const shareReferralLink = async () => {
    if (referralData?.share_link) {
      if (navigator.share) {
        try {
          await navigator.share({
            title: 'Join HearMeOut',
            text: 'Join me on HearMeOut for mental health support! Use my referral code.',
            url: referralData.share_link
          });
        } catch (err) {
          // User cancelled or error - fallback to copy
          navigator.clipboard.writeText(referralData.share_link);
          toast.success('Referral link copied!');
        }
      } else {
        navigator.clipboard.writeText(referralData.share_link);
        toast.success('Referral link copied!');
      }
    }
  };

  const handleDownloadInvoice = async (orderId) => {
    setDownloadingInvoice(orderId);
    try {
      const response = await billingAPI.downloadInvoice(orderId);
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `HearMeOut_Invoice_${orderId.substring(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Invoice downloaded successfully');
    } catch (error) {
      console.error('Failed to download invoice:', error);
      toast.error('Failed to download invoice');
    } finally {
      setDownloadingInvoice(null);
    }
  };

  const handleEmailInvoice = async (orderId) => {
    setEmailingInvoice(orderId);
    try {
      await billingAPI.emailInvoice(orderId);
      toast.success('Invoice sent to your email!');
    } catch (error) {
      console.error('Failed to email invoice:', error);
      toast.error(error.response?.data?.detail || 'Failed to send invoice email');
    } finally {
      setEmailingInvoice(null);
    }
  };

  const handleProfilePictureChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 12MB)
    if (file.size > 12 * 1024 * 1024) {
      toast.error('Image too large. Maximum size is 12MB');
      return;
    }

    setUploadingPicture(true);

    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64Image = event.target.result;
        
        try {
          await api.post('/me/profile-picture', { image: base64Image });
          setProfilePicture(base64Image);
          toast.success('Profile picture updated!');
          refreshUser();
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to upload picture');
        } finally {
          setUploadingPicture(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to process image');
      setUploadingPicture(false);
    }
  };

  // Get available upgrade plans based on current subscription
  const getUpgradePlans = () => {
    if (!subscription?.plan) return plans;
    const currentPlanIndex = PLAN_HIERARCHY.indexOf(subscription.plan);
    if (currentPlanIndex === -1) return plans;
    return plans.filter(plan => {
      const planIndex = PLAN_HIERARCHY.indexOf(plan.id);
      return planIndex > currentPlanIndex;
    });
  };

  const canUpgrade = () => {
    if (!subscription?.plan) return true;
    return subscription.plan !== 'yearly';
  };

  const handleCancelSubscription = async () => {
    setCancellingSubscription(true);
    try {
      await billingAPI.cancelSubscription();
      toast.success('Subscription cancelled successfully');
      setShowCancelModal(false);
      await fetchProfileData();
      if (refreshUser) await refreshUser();
      // Reload page to reflect role change
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancellingSubscription(false);
    }
  };

  const handleSubscribe = async (planId) => {
    setProcessingPlan(planId);
    setLoading(true);

    try {
      // Load Razorpay script if not loaded
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Failed to load payment gateway');
        return;
      }

      // Create order
      const response = await billingAPI.createOrder(planId);
      const { order_id, amount, currency, key_id, plan_name } = response.data;

      // Configure Razorpay options
      const options = {
        key: key_id,
        amount: amount,
        currency: currency,
        name: 'HearMeOut',
        description: `${plan_name} Subscription`,
        order_id: order_id,
        handler: async function (response) {
          try {
            // Verify payment
            const verifyData = {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan: planId
            };
            
            const result = await billingAPI.verifyPayment(verifyData);
            
            if (result.data.success) {
              toast.success('Subscription activated successfully!');
              // Refresh user data and subscription
              await fetchProfileData();
              if (refreshUser) await refreshUser();
            }
          } catch (error) {
            toast.error('Payment verification failed');
            console.error('Verification error:', error);
          }
        },
        prefill: {
          name: user?.full_name || '',
          email: user?.email || '',
        },
        theme: {
          color: '#7C9A92'
        },
        modal: {
          ondismiss: function() {
            setLoading(false);
            setProcessingPlan(null);
          }
        }
      };

      // Open Razorpay checkout
      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', function (response) {
        toast.error('Payment failed: ' + response.error.description);
        setLoading(false);
        setProcessingPlan(null);
      });
      razorpay.open();

    } catch (error) {
      toast.error('Failed to create order');
      console.error('Order creation error:', error);
    } finally {
      setLoading(false);
      setProcessingPlan(null);
    }
  };

  return (
    <div className="min-h-screen py-8" data-testid="profile-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold mb-8 text-sunrise-black" style={{ fontFamily: 'Nunito, sans-serif' }}>Profile & Settings</h1>

        {/* User Info */}
        <div className="feature-card mb-8" data-testid="user-info-card">
          <div className="flex items-center space-x-4 mb-6">
            {/* Profile Picture with Upload */}
            <div className="relative">
              <div 
                className="w-20 h-20 rounded-full flex items-center justify-center overflow-hidden cursor-pointer group"
                style={{ background: 'rgba(255, 140, 0, 0.15)' }}
                onClick={() => fileInputRef.current?.click()}
              >
                {profilePicture ? (
                  <img 
                    src={profilePicture} 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="w-10 h-10" style={{ color: '#FF8C00' }} />
                )}
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-full">
                  {uploadingPicture ? (
                    <Loader2 className="w-6 h-6 text-white animate-spin" />
                  ) : (
                    <Camera className="w-6 h-6 text-white" />
                  )}
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleProfilePictureChange}
                className="hidden"
                data-testid="profile-picture-input"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center text-white transition-colors"
                style={{ background: '#FF8C00' }}
                disabled={uploadingPicture}
              >
                <Upload className="w-3.5 h-3.5" />
              </button>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-sunrise-black" style={{ fontFamily: 'Nunito, sans-serif' }}>{user?.full_name}</h2>
              <p className="text-sunrise-medium">{user?.email}</p>
              {user?.email_verified && (
                <span className="inline-flex items-center gap-1 text-xs mt-1" style={{ color: '#22C55E' }}>
                  <CheckCircle className="w-3 h-3" /> Email Verified
                </span>
              )}
              <p className="text-xs mt-1 text-sunrise-light">Click profile picture to upload</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 pt-4" style={{ borderTop: '1px solid var(--card-border)' }}>
            <div>
              <p className="text-sm text-sunrise-light">Age</p>
              <p className="font-semibold text-sunrise-black">{user?.age} years</p>
            </div>
            <div>
              <p className="text-sm text-sunrise-light">Timezone</p>
              <p className="font-semibold text-sunrise-black">{user?.timezone || 'UTC'}</p>
            </div>
          </div>
        </div>

        {/* Subscription */}
        <div className="feature-card mb-8" data-testid="subscription-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <Crown className="w-6 h-6" style={{ color: '#FFA500' }} />
              <h2 className="text-xl font-bold text-sunrise-black" style={{ fontFamily: 'Nunito, sans-serif' }}>Subscription</h2>
            </div>
            {isPro && (
              <span className="flex items-center gap-1 px-3 py-1 text-sm font-bold rounded-full" style={{ background: 'rgba(34, 197, 94, 0.15)', color: '#22C55E' }}>
                <Sparkles className="w-4 h-4" />
                Pro
              </span>
            )}
          </div>
          
          {subscription?.status === 'active' || isPro ? (
            <div>
              {/* Active subscription info */}
              <div className="p-4 bg-green-500/10 rounded-xl border border-green-500/30 mb-4">
                <p className="font-semibold text-green-400 mb-1">Active Subscription</p>
                <p className="text-sm text-sunrise-black">Plan: {subscription?.plan_details?.name || subscription?.plan || 'Pro'}</p>
                {subscription?.plan_details?.price_usd && (
                  <p className="text-sm text-sunrise-medium">${subscription.plan_details.price_usd}/{subscription.plan === 'weekly' ? 'week' : subscription.plan === 'monthly' ? 'month' : subscription.plan === 'quarterly' ? '3 months' : 'year'}</p>
                )}
                {subscription?.end_date && (
                  <p className="text-xs text-sunrise-light mt-2">
                    Valid until: {new Date(subscription.end_date).toLocaleDateString()}
                  </p>
                )}
              </div>

              {/* Upgrade and Cancel buttons for Pro users */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  disabled={!canUpgrade()}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-full font-semibold transition-colors ${
                    canUpgrade()
                      ? 'bg-primary text-white hover:bg-primary/90'
                      : 'bg-muted text-muted-foreground cursor-not-allowed'
                  }`}
                  data-testid="upgrade-subscription-btn"
                >
                  <ArrowUpCircle className="w-5 h-5" />
                  {canUpgrade() ? 'Upgrade Subscription' : 'Already on Best Plan'}
                </button>
                <button
                  onClick={() => setShowCancelModal(true)}
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-full font-semibold bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors"
                  data-testid="cancel-subscription-btn"
                >
                  <XCircle className="w-5 h-5" />
                  Cancel Subscription
                </button>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-sunrise-medium mb-4">Unlock premium features with a subscription</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {plans.map((plan) => (
                  <div 
                    key={plan.id} 
                    className="p-4 rounded-xl transition-colors"
                    style={{ border: '2px solid var(--card-border)' }}
                    data-testid="subscription-plan"
                  >
                    <h3 className="font-bold text-sunrise-black mb-1">{plan.name}</h3>
                    <div className="flex items-baseline space-x-1 mb-1">
                      <span className="text-3xl font-bold" style={{ fontFamily: 'Nunito, sans-serif', color: '#FF8C00' }}>
                        ${plan.price_usd}
                      </span>
                      <span className="text-sm text-sunrise-light">/ {plan.id === 'weekly' ? 'week' : plan.id === 'monthly' ? 'month' : plan.id === 'quarterly' ? '3 months' : 'year'}</span>
                    </div>
                    <p className="text-xs text-sunrise-light mb-3">{plan.amount_display}</p>
                    <button
                      onClick={() => handleSubscribe(plan.id)}
                      disabled={loading && processingPlan === plan.id}
                      className="w-full py-2 rounded-full text-white font-semibold disabled:opacity-50 flex items-center justify-center gap-2 btn-sunrise"
                      data-testid="subscribe-button"
                    >
                      {loading && processingPlan === plan.id ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        'Subscribe'
                      )}
                    </button>
                  </div>
                ))}
              </div>
              <p className="text-xs text-sunrise-light mt-4 text-center">
                Powered by Razorpay • Secure payments
              </p>
            </div>
          )}
        </div>

        {/* Order History */}
        <div className="feature-card mb-8" data-testid="order-history-card">
          <div className="flex items-center space-x-3 mb-6">
            <Receipt className="w-6 h-6 text-blue-400" />
            <h2 className="text-xl font-bold text-sunrise-black" style={{ fontFamily: 'Nunito, sans-serif' }}>Order History</h2>
          </div>
          
          {loadingOrders ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#FF8C00' }} />
            </div>
          ) : orderHistory.length === 0 ? (
            <div className="text-center py-8">
              <ShoppingBag className="w-12 h-12 mx-auto mb-3 text-sunrise-light" />
              <p className="text-sunrise-medium">No orders yet</p>
              <p className="text-sm text-sunrise-light mt-1">Your purchase history will appear here</p>
            </div>
          ) : (
            <div className="space-y-4">
              {orderHistory.map((order) => (
                <div 
                  key={order.id}
                  className="p-4 rounded-xl"
                  style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}
                  data-testid={`order-item-${order.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {order.type === 'subscription' ? (
                          <Crown className="w-4 h-4 text-yellow-500" />
                        ) : (
                          <ShoppingBag className="w-4 h-4 text-blue-500" />
                        )}
                        <span className="font-semibold text-sunrise-black">{order.item_name}</span>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-sm text-sunrise-light">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {order.created_at ? new Date(order.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : 'N/A'}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                          order.status === 'active' || order.status === 'paid' 
                            ? 'bg-green-500/20 text-green-500'
                            : order.status === 'cancelled'
                            ? 'bg-red-500/20 text-red-500'
                            : 'bg-yellow-500/20 text-yellow-500'
                        }`}>
                          {order.status?.toUpperCase()}
                        </span>
                        {order.discount_code && (
                          <span className="px-2 py-0.5 bg-purple-500/20 text-purple-500 rounded-full text-xs">
                            {order.discount_percent}% OFF
                          </span>
                        )}
                      </div>
                      <div className="mt-2 text-sm">
                        <span className="text-sunrise-light">Order ID: </span>
                        <span className="text-sunrise-black font-mono text-xs">{order.id?.substring(0, 8)}...</span>
                      </div>
                    </div>
                    <div className="text-right ml-4">
                      <p className="font-bold text-lg text-sunrise-black">
                        ₹{(order.amount / 100).toFixed(0)}
                      </p>
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={() => handleDownloadInvoice(order.id)}
                          disabled={downloadingInvoice === order.id}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
                          style={{ background: 'rgba(255, 140, 0, 0.15)', color: '#FF8C00' }}
                          data-testid={`download-invoice-${order.id}`}
                          title="Download Invoice"
                        >
                          {downloadingInvoice === order.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Download className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleEmailInvoice(order.id)}
                          disabled={emailingInvoice === order.id}
                          className="flex items-center gap-1 px-3 py-1.5 bg-blue-500/10 text-blue-500 rounded-lg text-sm font-semibold hover:bg-blue-500/20 transition-colors disabled:opacity-50"
                          data-testid={`email-invoice-${order.id}`}
                          title="Email Invoice"
                        >
                          {emailingInvoice === order.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Mail className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Referral Program */}
        <div className="feature-card mb-8" data-testid="referral-card">
          <div className="flex items-center space-x-3 mb-6">
            <Gift className="w-6 h-6 text-pink-400" />
            <h2 className="text-xl font-bold text-sunrise-black" style={{ fontFamily: 'Nunito, sans-serif' }}>Refer & Earn</h2>
          </div>
          
          {loadingReferral ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#FF8C00' }} />
            </div>
          ) : referralData ? (
            <div className="space-y-4">
              {/* Referral Code Display */}
              <div className="p-4 bg-gradient-to-r from-pink-500/10 to-purple-500/10 rounded-xl border border-pink-500/20">
                <p className="text-sm text-sunrise-light mb-2">Your Referral Code</p>
                <div className="flex items-center gap-3">
                  <span className="text-2xl font-bold text-sunrise-black tracking-wider font-mono" data-testid="referral-code">
                    {referralData.code}
                  </span>
                  <button
                    onClick={copyReferralCode}
                    className="p-2 rounded-lg transition-colors"
                    style={{ background: 'rgba(255, 140, 0, 0.15)', color: '#FF8C00' }}
                    title="Copy Code"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    onClick={shareReferralLink}
                    className="p-2 bg-blue-500/10 text-blue-500 rounded-lg hover:bg-blue-500/20 transition-colors"
                    title="Share Link"
                  >
                    <Share2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              {/* Stats Grid */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-xl text-center" style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
                  <Users className="w-5 h-5 text-blue-400 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-sunrise-black">{referralData.total_referrals || 0}</p>
                  <p className="text-xs text-sunrise-light">Total Referrals</p>
                </div>
                <div className="p-3 rounded-xl text-center" style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
                  <CheckCircle className="w-5 h-5 text-green-400 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-sunrise-black">{referralData.successful_referrals || 0}</p>
                  <p className="text-xs text-sunrise-light">Converted</p>
                </div>
                <div className="p-3 rounded-xl text-center" style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
                  <Crown className="w-5 h-5 text-yellow-400 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-sunrise-black">₹{(referralData.credits_available || 0) / 100}</p>
                  <p className="text-xs text-sunrise-light">Credits</p>
                </div>
              </div>
              
              {/* How it works */}
              <div className="p-4 rounded-xl" style={{ background: 'rgba(255, 165, 0, 0.08)' }}>
                <p className="text-sm font-semibold text-sunrise-black mb-2">How it works</p>
                <ul className="text-xs text-sunrise-light space-y-1">
                  <li>• Share your referral code with friends</li>
                  <li>• When they subscribe, you earn ₹100 in credits</li>
                  <li>• Use credits towards your next subscription</li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Gift className="w-12 h-12 text-sunrise-light mx-auto mb-3" />
              <p className="text-sunrise-medium">Loading referral program...</p>
            </div>
          )}
        </div>

        {/* Doctor Connections Section (for patients only) */}
        {!isDoctor && (
          <div className="feature-card mb-8" data-testid="doctor-connections-card">
            <div className="flex items-center space-x-3 mb-6">
              <Stethoscope className="w-6 h-6 text-cyan-400" />
              <h2 className="text-xl font-bold text-sunrise-black" style={{ fontFamily: 'Nunito, sans-serif' }}>Doctor Connections</h2>
            </div>

            {/* Connected Doctors */}
            {doctorConnections.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-sunrise-black mb-3">Connected Doctors</h3>
                <div className="space-y-2">
                  {doctorConnections.map((conn) => (
                    <div key={conn.id} className="flex items-center justify-between p-3 rounded-xl" style={{ background: 'rgba(255, 165, 0, 0.08)' }}>
                      <div>
                        <p className="font-medium text-sunrise-black">Dr. {conn.doctor_name}</p>
                        <p className="text-xs text-sunrise-light">{conn.doctor_email}</p>
                      </div>
                      <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-xs">Connected</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Generate Connection Code */}
            <div className="space-y-4">
              <div className="p-4 bg-cyan-500/5 border border-cyan-500/20 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h4 className="font-medium text-sunrise-black flex items-center gap-2">
                      <Link2 className="w-4 h-4 text-cyan-400" />
                      Connect with a Doctor
                    </h4>
                    <p className="text-xs text-sunrise-light mt-1">Generate a code to share with your doctor</p>
                  </div>
                  <button
                    onClick={generateConnectionCode}
                    disabled={generatingCode}
                    className="px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 disabled:opacity-50 text-sm font-medium"
                  >
                    {generatingCode ? 'Generating...' : 'Generate Code'}
                  </button>
                </div>
                
                {connectionCode && (
                  <div className="mt-3 p-3 bg-background rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-2xl font-mono font-bold text-cyan-400 tracking-widest">{connectionCode.code}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Expires: {new Date(connectionCode.expires_at).toLocaleString()}
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(connectionCode.code);
                          toast.success('Code copied!');
                        }}
                        className="p-2 bg-cyan-500/20 text-cyan-400 rounded-lg hover:bg-cyan-500/30"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Generate Doctor Access PIN for Vault */}
              <div className="p-4 bg-violet-500/5 border border-violet-500/20 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h4 className="font-medium text-foreground flex items-center gap-2">
                      <Key className="w-4 h-4 text-violet-400" />
                      Vault Access PIN for Doctor
                    </h4>
                    <p className="text-xs text-muted-foreground mt-1">Allow your doctor to view your private vault (24h access)</p>
                  </div>
                  <button
                    onClick={generateDoctorAccessPin}
                    disabled={generatingPin}
                    className="px-4 py-2 bg-violet-500 text-white rounded-lg hover:bg-violet-600 disabled:opacity-50 text-sm font-medium"
                  >
                    {generatingPin ? 'Generating...' : 'Generate PIN'}
                  </button>
                </div>
                
                {doctorAccessPin && (
                  <div className="mt-3 p-3 bg-background rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-2xl font-mono font-bold text-violet-400 tracking-widest">{doctorAccessPin.pin}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Expires: {new Date(doctorAccessPin.expires_at).toLocaleString()}
                        </p>
                        <p className="text-xs text-orange-400 mt-1">⚠️ This PIN can only be used once</p>
                      </div>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(doctorAccessPin.pin);
                          toast.success('PIN copied!');
                        }}
                        className="p-2 bg-violet-500/20 text-violet-400 rounded-lg hover:bg-violet-500/30"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Upgrade Modal */}
        {showUpgradeModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="upgrade-modal">
            <div className="bg-card rounded-2xl p-6 max-w-md w-full border border-border">
              <h3 className="text-xl font-bold text-foreground mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Upgrade Your Plan
              </h3>
              <p className="text-muted-foreground mb-6">
                Choose a higher plan to unlock more value and savings.
              </p>
              
              {getUpgradePlans().length > 0 ? (
                <div className="space-y-3 mb-6">
                  {getUpgradePlans().map((plan) => (
                    <div 
                      key={plan.id}
                      className="p-4 border border-border rounded-xl hover:border-primary transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-bold text-foreground">{plan.name}</h4>
                        <span className="text-lg font-bold text-primary">${plan.price_usd}</span>
                      </div>
                      <button
                        onClick={() => {
                          setShowUpgradeModal(false);
                          handleSubscribe(plan.id);
                        }}
                        className="w-full py-2 rounded-full bg-primary text-white font-semibold hover:bg-primary/90"
                      >
                        Upgrade to {plan.name.replace(' Plan', '')}
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground mb-6">
                  You're already on the best plan!
                </p>
              )}
              
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="w-full py-3 rounded-full bg-muted text-foreground font-semibold hover:bg-muted/80"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Cancel Subscription Modal */}
        {showCancelModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="cancel-modal">
            <div className="bg-card rounded-2xl p-6 max-w-md w-full border border-border">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-destructive/20 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-destructive" />
                </div>
                <h3 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Nunito, sans-serif' }}>
                  Cancel Subscription?
                </h3>
              </div>
              
              <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-4 mb-6">
                <p className="text-foreground font-medium mb-2">
                  You will lose access to premium features:
                </p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Medication management</li>
                  <li>• Just Talk peer support</li>
                  <li>• AI Companion (Amigo)</li>
                  <li>• Wellness Store access</li>
                  <li>• MindPulse™ wellness alerts</li>
                </ul>
              </div>
              
              <p className="text-sm text-muted-foreground mb-6">
                Your account will be downgraded to a free user with basic features only.
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="flex-1 py-3 rounded-full bg-muted text-foreground font-semibold hover:bg-muted/80"
                  disabled={cancellingSubscription}
                >
                  Keep Subscription
                </button>
                <button
                  onClick={handleCancelSubscription}
                  disabled={cancellingSubscription}
                  className="flex-1 py-3 rounded-full bg-destructive text-white font-semibold hover:bg-destructive/90 flex items-center justify-center gap-2"
                  data-testid="confirm-cancel-btn"
                >
                  {cancellingSubscription ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Cancelling...
                    </>
                  ) : (
                    'Yes, Cancel'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Consents Info */}
        <div className="feature-card">
          <h2 className="text-xl font-bold text-sunrise-black mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>Privacy & Consents</h2>
          <div className="space-y-3 text-sm">
            <div className="flex items-center space-x-2">
              <Brain className="w-4 h-4 text-green-400" />
              <span className="text-sunrise-black">Data usage consent: Active</span>
            </div>
            <div className="flex items-center space-x-2">
              <Brain className="w-4 h-4 text-green-400" />
              <span className="text-sunrise-black">Chat moderation consent: Active</span>
            </div>
            <div className="flex items-center space-x-2">
              <Brain className="w-4 h-4 text-green-400" />
              <span className="text-sunrise-black">Emergency disclaimer: Acknowledged</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
